import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { getCachedData, refreshCache } from '../store';
import { formatMoneyPrecise, todayISO } from '../dateUtils';
import { accountBalance, debtBalance, totalBalance } from '../balance';
import AccountForm from './AccountForm';
import Modal from './Modal';

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  // Баланс для формы редактирования счёта
  const [editingBalance, setEditingBalance] = useState('');
  // Долги
  const [debtOp, setDebtOp] = useState(null); // { account, kind }
  const [editingDebt, setEditingDebt] = useState(null);
  const [editingDebtBalance, setEditingDebtBalance] = useState('');
  // Форма добавления нового человека (долговой счёт)
  const [showPersonForm, setShowPersonForm] = useState(false);

  const loadData = async () => {
    const [accs, tx] = await Promise.all([api.getAccounts(), api.getTransactions()]);
    setAccounts(accs);
    setTransactions(tx);
  };

  useEffect(() => {
    const cached = getCachedData();
    if (cached && Array.isArray(cached.accounts)) setAccounts(cached.accounts);
    if (cached && Array.isArray(cached.transactions)) setTransactions(cached.transactions);
    loadData();
  }, []);

  const regularAccounts = accounts.filter(a => !a.isDebt);
  const debtAccounts = accounts.filter(a => a.isDebt);

  const openEdit = (account) => {
    setEditingAccount(account);
    setEditingBalance(Math.round(accountBalance(transactions, account.id) * 100) / 100);
    setShowForm(true);
  };

  // Сохранение счёта: при редактировании создаём корректировку, если изменился остаток
  const handleAccountSave = async (data) => {
    if (editingAccount) {
      await api.updateAccount(editingAccount.id, data);
      if (typeof data.balance === 'number') {
        const current = accountBalance(transactions, editingAccount.id);
        const diff = Math.round((data.balance - current) * 100) / 100;
        if (diff !== 0) {
          const type = diff > 0 ? 'income' : 'expense';
          await api.createTransaction({
            type,
            categoryId: null,
            amount: Math.round(Math.abs(diff) * 100) / 100,
            date: todayISO(),
            description: 'Корректировка баланса',
            accountId: editingAccount.id,
            periodId: null,
          });
        }
      }
    } else {
      const created = await api.createAccount(data);
      // Начальный остаток при создании счёта — создаём операцию «Начальный баланс»
      if (typeof data.balance === 'number' && data.balance !== 0) {
        const type = data.balance > 0 ? 'income' : 'expense';
        await api.createTransaction({
          type,
          categoryId: null,
          amount: Math.abs(data.balance),
          date: todayISO(),
          description: 'Начальный баланс',
          accountId: created.id,
          periodId: null,
        });
      }
    }
    setShowForm(false);
    setEditingAccount(null);
    setEditingBalance('');
    loadData();
    refreshCache();
    window.dispatchEvent(new Event('data:changed'));
  };

  // Переключить «Учитывать в общем балансе»
  const toggleIncludeInBalance = async (account) => {
    await api.updateAccount(account.id, { includeInBalance: !account.includeInBalance });
    await loadData();
    refreshCache();
    window.dispatchEvent(new Event('data:changed'));
  };

  // Операция по долгу: kind = lend | borrow | return | collect
  const handleDebtOp = async (kind) => {
    if (!debtOp) return;
    const amount = parseFloat(debtOp.amount);
    const realAccountId = parseInt(debtOp.accountId);
    if (isNaN(amount) || amount <= 0 || !realAccountId) {
      alert('Укажите сумму и выберите счёт');
      return;
    }
    await api.createTransaction({
      type: 'debt',
      accountId: realAccountId,
      debtAccountId: debtOp.account.id,
      debtKind: kind,
      amount,
      date: todayISO(),
      description: '',
      periodId: null,
    });
    setDebtOp(null);
    await loadData();
    refreshCache();
    window.dispatchEvent(new Event('data:changed'));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить счёт? Транзакции по нему останутся, но счёт будет убран.')) {
      await api.deleteAccount(id);
      loadData();
      refreshCache();
      window.dispatchEvent(new Event('data:changed'));
    }
  };

  // Создать человека в долгах (счёт-долг)
  const handlePersonSave = async (data) => {
    try {
      await api.createAccount(data);
    } catch (err) {
      console.error(err);
    }
    setShowPersonForm(false);
    await loadData();
    refreshCache();
    window.dispatchEvent(new Event('data:changed'));
  };

  // Редактировать человека в долгах / сумму долга: создаём корректировку долга
  const handleDebtEditSave = async (data) => {
    if (!editingDebt) return;
    await api.updateAccount(editingDebt.id, { name: data.name, person: data.name, isDebt: true, includeInBalance: false });
    if (typeof data.balance === 'number') {
      const current = debtBalance(transactions, editingDebt.id);
      const diff = data.balance - current;
      if (diff !== 0) {
        // Корректировка долга: не трогаем реальные счета, поэтому общий и суточный баланс не меняются
        await api.createTransaction({
          type: 'debt',
          accountId: editingDebt.id,
          debtAccountId: editingDebt.id,
          debtKind: diff > 0 ? 'lend' : 'borrow',
          amount: Math.abs(diff),
          date: todayISO(),
          description: 'Корректировка долга',
          periodId: null,
        });
      }
    }
    setEditingDebt(null);
    setEditingDebtBalance('');
    await loadData();
    refreshCache();
    window.dispatchEvent(new Event('data:changed'));
  };

  return (
    <div className="content">
      <h1>Счета</h1>

      <div className="gradient-card" style={{ marginBottom: 16 }}>
        <div className="card-title">Общий баланс</div>
        <div className="big-number">{formatMoneyPrecise(totalBalance(accounts, transactions))}</div>
        <div className="period-meta">
          <span>Учитываются счета с галочкой «в общем балансе»</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => { setShowForm(true); setEditingAccount(null); }}>+ Новый счёт</button>
      </div>

      {showForm && (
        <AccountForm
          account={editingAccount}
          currentBalance={editingBalance}
          onSave={handleAccountSave}
          onCancel={() => { setShowForm(false); setEditingAccount(null); setEditingBalance(''); }}
        />
      )}

      <h2 className="section-title">Счета</h2>
      {regularAccounts.length === 0 && (
        <div className="card" style={{ color: 'var(--text-dim)', textAlign: 'center' }}>
          Пока нет счетов. Добавь свой первый счёт.
        </div>
      )}
      <ul className="list">
        {regularAccounts.map(a => (
          <li key={a.id} className="list-item">
            <div className="list-item-left">
              <span className="cat-icon">💳</span>
              <div>
                <div className="list-item-title">{a.name}</div>
                <div className="list-item-sub">Остаток: {formatMoneyPrecise(accountBalance(transactions, a.id))}</div>
                {a.isCredit && (() => {
                  const bal = accountBalance(transactions, a.id);
                  const limit = Number(a.creditLimit) || 0;
                  // Остаток = сколько доступно по карте, долг банку = лимит − остаток
                  const owed = Math.max(0, limit - bal);
                  return (
                    <>
                      <div className="list-item-sub">Лимит: {formatMoneyPrecise(limit)}</div>
                      <div className="list-item-sub">Долг банку: {formatMoneyPrecise(owed)}</div>
                    </>
                  );
                })()}
                <label className="include-check" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12.5 }}>
                  <input
                    type="checkbox"
                    checked={a.includeInBalance !== false}
                    onChange={() => toggleIncludeInBalance(a)}
                  />
                  Учитывать в общем балансе
                </label>
              </div>
            </div>
            <div className="actions">
              <button className="small-icon" onClick={() => openEdit(a)}>✏️</button>
              <button className="small-icon danger" onClick={() => handleDelete(a.id)}>🗑️</button>
            </div>
          </li>
        ))}
      </ul>

      <h2 className="section-title">Долги</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <button onClick={() => setShowPersonForm(true)}>＋ Добавить человека</button>
      </div>
      {showPersonForm && (
        <AccountForm
          isDebtPerson
          onSave={handlePersonSave}
          onCancel={() => setShowPersonForm(false)}
        />
      )}
      {debtAccounts.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-dim)', textAlign: 'center' }}>
          Долгов пока нет. Добавь человека, с которым ведёшь расчёты.
        </div>
      ) : (
        <ul className="list">
          {debtAccounts.map(a => {
            const bal = debtBalance(transactions, a.id);
            return (
              <li key={a.id} className="list-item">
                <div className="list-item-left">
                  <span className="cat-icon">🤝</span>
                  <div>
                    <div className="list-item-title">{a.person || a.name}</div>
                    <div className="list-item-sub">
                      {bal > 0 ? `Должен вам: ${formatMoneyPrecise(bal)}` : bal < 0 ? `Вы должны: ${formatMoneyPrecise(Math.abs(bal))}` : 'Взаиморасчёты: 0.00 ₽'}
                    </div>
                  </div>
                </div>
                <div className="actions" style={{ gap: 4 }}>
                  <button
                    className="small-icon"
                    onClick={() => setDebtOp({ account: a, kind: bal > 0 ? 'lend' : 'return', amount: '', accountId: '' })}
                    title="Операция по долгу (дал в долг, вернул, взял, получил)"
                  >➕</button>
                  <button
                    className="small-icon"
                    title="Редактировать сумму долга"
                    onClick={() => { setEditingDebt(a); setEditingDebtBalance(debtBalance(transactions, a.id)); }}
                  >✏️</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editingDebt && (
        <AccountForm
          account={editingDebt}
          isDebtPerson
          currentBalance={editingDebtBalance}
          onSave={handleDebtEditSave}
          onCancel={() => { setEditingDebt(null); setEditingDebtBalance(''); }}
        />
      )}

      {debtOp && (
        <Modal title={debtOp.account.person || debtOp.account.name} onClose={() => setDebtOp(null)}>
          <div className="form-group">
            <label>Операция</label>
            <select value={debtOp.kind} onChange={(e) => setDebtOp({ ...debtOp, kind: e.target.value })}>
              <option value="lend">Дал в долг (деньги ушли) ⬇️</option>
              <option value="return">Вернул долг (деньги ушли) ⬇️</option>
              <option value="borrow">Взял в долг (деньги пришли) ⬆️</option>
              <option value="collect">Получил обратно (деньги пришли) ⬆️</option>
            </select>
          </div>
          <div className="form-group">
            <label>Сумма</label>
            <input type="number" inputMode="decimal" step="0.01" value={debtOp.amount} onChange={(e) => setDebtOp({ ...debtOp, amount: e.target.value })} autoFocus />
          </div>
          <div className="form-group">
            <label>С какого/на какой счёт</label>
            <select value={debtOp.accountId} onChange={(e) => setDebtOp({ ...debtOp, accountId: e.target.value })}>
              <option value="">Выберите счёт</option>
              {regularAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => handleDebtOp(debtOp.kind)}>Провести</button>
            <button type="button" className="secondary" onClick={() => setDebtOp(null)}>Отмена</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

